import { useState } from 'react';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/ui/Tooltip';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';
import {
  Target, ChevronDown, ChevronRight, CheckCircle2, Loader2, Zap,
} from 'lucide-react';
import { WORKSTREAM_COLORS } from './constants';
import type { StrategicWorkstream } from '@/hooks/useStrategicStream';

export function EngagementManagerNode({
  isActive,
  isComplete,
  workstreamCount,
  workstreams,
}: {
  isActive: boolean;
  isComplete: boolean;
  workstreamCount: number;
  workstreams: StrategicWorkstream[];
}) {
  const [showWorkstreams, setShowWorkstreams] = useState(false);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'relative flex items-center border transition-all duration-500',
          'backdrop-blur-md bg-gradient-to-br rounded-xl',
          isSmallScreen ? 'gap-2 p-3' : 'gap-3 p-4',
          isActive
            ? 'from-purple-500/15 to-purple-600/5 border-purple-500/40 shadow-lg shadow-purple-500/10'
            : isComplete
            ? 'from-purple-500/10 to-purple-600/5 border-purple-500/30'
            : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
        )}
        data-active={isActive}
      >
        {(isActive || isComplete) && (
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-400/5 to-transparent pointer-events-none" />
        )}
        <div className={cn(
          'relative rounded-lg flex items-center justify-center transition-all duration-300',
          isSmallScreen ? 'w-8 h-8' : 'w-10 h-10',
          isActive ? 'bg-purple-500/25 shadow-inner' : isComplete ? 'bg-purple-500/20' : 'bg-surface-light/80'
        )}>
          {isActive ? (
            <Loader2 className={cn(
              "text-purple-400 animate-spin drop-shadow-[0_0_8px_rgba(168,85,247,0.5)]",
              isSmallScreen ? "w-4 h-4" : "w-5 h-5"
            )} />
          ) : isComplete ? (
            <>
              <Target className={cn("text-purple-400", isSmallScreen ? "w-4 h-4" : "w-5 h-5")} />
              <div className={cn(
                "absolute rounded-full bg-verdict-primary flex items-center justify-center shadow-lg shadow-verdict-primary/30",
                isSmallScreen ? "-top-0.5 -right-0.5 w-3.5 h-3.5" : "-top-1 -right-1 w-4 h-4"
              )}>
                <CheckCircle2 className={cn("text-black", isSmallScreen ? "w-2.5 h-2.5" : "w-3 h-3")} />
              </div>
            </>
          ) : (
            <Target className={cn("text-text-muted", isSmallScreen ? "w-4 h-4" : "w-5 h-5")} />
          )}
        </div>
        <div className="relative flex-1 min-w-0">
          <div className={cn(
            "font-mono uppercase text-text-secondary tracking-wider",
            isSmallScreen ? "text-xs" : "text-sm"
          )}>Engagement Manager <InfoTooltip content="Breaks your question into non-overlapping research tracks, like a consulting firm scoping a project." position="right" /></div>
          <div className={cn(
            "text-text-primary font-medium",
            isSmallScreen ? "text-sm" : "text-base"
          )}>
            {isActive ? (
              <span className="truncate block">{isSmallScreen ? 'Building MECE tree...' : 'Decomposing into MECE workstreams...'}</span>
            ) : (
              `Created ${workstreamCount} strategic workstreams`
            )}
          </div>
          {isActive && !isSmallScreen && (
            <div className="mt-1 text-sm text-purple-400/70 font-mono flex items-center gap-1">
              <span className="animate-pulse">|</span>
              Analyzing strategic dimensions...
            </div>
          )}
        </div>
        {isComplete && workstreams.length > 0 && (
          <button
            onClick={() => setShowWorkstreams(!showWorkstreams)}
            className={cn(
              "hover:bg-purple-500/10 rounded-lg transition-colors flex-shrink-0",
              isSmallScreen ? "p-1.5" : "p-2"
            )}
            title={showWorkstreams ? 'Hide workstream breakdown' : 'Show workstream breakdown'}
          >
            {showWorkstreams ? (
              <ChevronDown className={cn("text-purple-400", isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
            ) : (
              <ChevronRight className={cn("text-purple-400", isSmallScreen ? "w-3.5 h-3.5" : "w-4 h-4")} />
            )}
          </button>
        )}
      </div>

      {/* Animated Workstream Breakdown List */}
      {showWorkstreams && workstreams.length > 0 && (
        <div className={cn(
          "space-y-2 border-l-2 border-purple-500/20",
          isSmallScreen ? "ml-3 pl-2" : "ml-6 pl-4"
        )}>
          {workstreams.map((ws, idx) => {
            const color = WORKSTREAM_COLORS[idx % WORKSTREAM_COLORS.length];
            return (
              <div
                key={ws.id || idx}
                className={cn(
                  'rounded-lg border transition-all duration-500',
                  isSmallScreen ? 'p-2' : 'p-3',
                  'bg-purple-500/5 border-purple-500/20 opacity-100 translate-x-0'
                )}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className={cn("flex items-start", isSmallScreen ? "gap-1.5" : "gap-2")}>
                  <div
                    className={cn(
                      'rounded flex items-center justify-center font-bold flex-shrink-0 mt-0.5',
                      isSmallScreen ? 'w-6 h-6 text-xs' : 'w-7 h-7 text-sm'
                    )}
                    style={{ backgroundColor: `${color.text}20`, color: color.text }}
                  >
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={cn(
                      "font-medium text-text-primary flex items-center gap-1",
                      isSmallScreen ? "text-xs" : "text-sm gap-1.5"
                    )}>
                      <Zap className="w-3 h-3 flex-shrink-0" style={{ color: color.text }} />
                      <span className="break-words">{ws.name || `Workstream ${idx + 1}`}</span>
                    </div>
                    {ws.keyQuestions && ws.keyQuestions.length > 0 && (
                      <div className={cn(
                        "text-text-secondary mt-0.5",
                        isSmallScreen ? "text-xs" : "text-sm"
                      )}>{ws.keyQuestions[0]}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
